/* ──────────────────────────────────────────────────────────────────────────────
   Talep #814 — Yeni Satıcıya İade (backoffice giriş)  ·  /vendor-returns/new

   Saha belgesini (ReturnHeader, ReturnType=VEHICLE_TO_DEALER) BACKOFFICE'ten açar.
   Backend zaten hazırdı: POST /api/returns/SaveWithDetails "mobil + web ORTAK"tır
   ve satıcıya iadeyi TASLAK olsa bile ReturnableQuantityService.ValidateAsync'ten
   geçirir. Bu yüzden ekran serbest ürün girişi YAPMAZ — kalemler satıcının KALAN
   İADE EDİLEBİLİR alış faturası satırlarından seçilir (GET /api/vendors/{id}/
   purchase-documents). Her seçilen (fatura × ürün) satırı, miktarı o faturaya
   dağıtılmış TEK bir iade kalemi olur → Σ dağıtım = miktar kuralı kendiliğinden tutar,
   fiyat alış faturasından türetilir (iş kuralı 7), sunucu reddi minimuma iner.

   ══ ALT TİP ════════════════════════════════════════════════════════════════════
   ROBUST (sağlam): ek alan yok. SCRAP (bozuk): kalem başına HASAR NEDENİ + EN AZ 1
   FOTOĞRAF zorunlu (iş kuralı 5). Foto, kalem Id'sine (istemcide üretilir) önce
   yüklenir (POST /api/returns/detail-photo), sonra belge kaydedilir.

   ══ KAYIT ══════════════════════════════════════════════════════════════════════
   "Taslak Kaydet" (Status=Taslak): stok/cari DOKUNULMAZ. "Kes ve Gönder"
   (Status=Gönderildi): stok araçtan düşer, satıcı carisi işlenir, muhasebe faturası
   (Invoice Type=3) üretilir.
   ────────────────────────────────────────────────────────────────────────────── */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { s, authHeaders, Toast } from './listPageStyles';
import { RelationPicker } from './ui';
import { RELATION_CONFIGS } from './relationConfigs';
import { unwrapLookupItems } from './c360';
import { usePageMeta } from '../hooks/usePageMeta';
import { usePageActions } from '../contexts/PermissionContext';
import { toDateTimeOffset } from '../utils/format';
import { useStockBalances, StockCell } from './stockBalance';
import {
  API_BASE, RETURN_TYPE_VENDOR, RETURN_STATUS, SUB_TYPE, SUB_TYPE_OPTIONS,
  DAMAGE_REASON_OPTIONS, fmtMoney, fmtQty, fmtDate, unwrap,
} from './returnShared';

const VENDORS_API = '/api/vendors';

function todayInputValue() {
  const d = new Date();
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 10);
}

function newKey() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `k_${Math.floor(Math.random() * 1e9)}_${performance.now()}`;
}

/**
 * Görseli tarayıcıda küçültüp JPEG data URI'ye çevirir — data URI JSON gövdesinde gider,
 * ham telefon fotoğrafı istek boyutu limitini aşabilirdi. Mobil foto boyutlarıyla uyumlu.
 */
function fileToCompressedDataUri(file, maxDim = 1280, quality = 0.72) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Dosya okunamadı'));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error('Görsel çözümlenemedi'));
      img.onload = () => {
        let { width, height } = img;
        if (width > maxDim || height > maxDim) {
          const scale = maxDim / Math.max(width, height);
          width = Math.round(width * scale);
          height = Math.round(height * scale);
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', quality));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

// data URI'nin yaklaşık byte boyutu (base64 → 3/4).
function approxBytes(dataUri) {
  const i = dataUri.indexOf('base64,');
  const b64 = i >= 0 ? dataUri.slice(i + 7) : dataUri;
  return Math.round((b64.length * 3) / 4);
}

const card = {
  background: 'var(--card-bg, #fff)',
  border: '1px solid var(--border-color, rgba(15, 23, 42, 0.10))',
  borderRadius: 12,
  padding: 18,
  marginBottom: 16,
};
const sectionTitle = { margin: '0 0 14px', fontSize: 15, fontWeight: 800, color: 'var(--text-primary)' };
const th = { padding: '8px 8px', fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', textAlign: 'left', whiteSpace: 'nowrap' };
const td = { padding: '8px 8px', fontSize: 13, borderTop: '1px solid var(--border-color, rgba(15,23,42,0.06))', verticalAlign: 'top' };
const num = { textAlign: 'right' };
const mono = { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontWeight: 700 };
const muted = { color: 'var(--text-secondary, #6b7280)' };
const danger = { color: 'var(--danger, #e2445c)', fontWeight: 700 };

export function VendorReturnCreatePage() {
  const navigate = useNavigate();
  // Menü aksiyon hakkı: bu ekran yalnız YENİ belge açar → hem Taslak hem Kes, Add hakkı ister.
  const { canAdd } = usePageActions();
  usePageMeta('Yeni Satıcıya İade', 'Backoffice iade belgesi — kalemler satıcının kalan iade edilebilir alış faturalarından seçilir.');

  const [toast, setToast] = useState(null);
  const showToast = useCallback((type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 4000);
  }, []);

  // Etiket eşlemesi için küçük master listeleri (RelationPicker yalnız value döndürür).
  const [vendors, setVendors] = useState([]);
  const [representatives, setRepresentatives] = useState([]);
  const [warehouses, setWarehouses] = useState([]);

  // Başlık
  const [vendorId, setVendorId] = useState('');
  const [representativeId, setRepresentativeId] = useState('');
  const [warehouseId, setWarehouseId] = useState('');
  const [subType, setSubType] = useState('ROBUST');
  const [docDate, setDocDate] = useState(todayInputValue());
  const [notes, setNotes] = useState('');

  // Satıcının iade edilebilir alış faturası satırları + eklenen kalemler
  const [returnable, setReturnable] = useState([]);
  const [loadingReturnable, setLoadingReturnable] = useState(false);
  const [lines, setLines] = useState([]);
  const [saving, setSaving] = useState(false);

  const isScrap = subType === 'SCRAP';

  // Talep 748 — Seçili araç deposundaki stok, hem "iade edilebilir" listesinde (eklemeden
  // ÖNCE) hem de eklenmiş iade kalemlerinde INFO olarak görünür. "Kalan" alış faturasından
  // iade edilebilir miktardır; buradaki rozet ise ELDE gerçekten ne olduğunu söyler —
  // ikisi farklı sorulara cevap verir, bu yüzden yan yana dururlar.
  //
  // DİKKAT: stok anahtarındaki VendorId = DEPONUN distribütörüdür (Warehouse.VendorId),
  // bu ekrandaki `vendorId` (satıcı cari) DEĞİLDİR. İkisi karıştırılırsa bakiye hep boş
  // döner (bkz. StockDocumentPage.js:143 aynı Warehouse.VendorId ilişkisi).
  const warehouseVendorId = useMemo(() => {
    const w = warehouses.find((x) => String(x.id) === String(warehouseId));
    return w ? (w.vendorId ?? w.VendorId ?? '') : '';
  }, [warehouses, warehouseId]);
  const stockProductIds = useMemo(
    () => [...returnable.map((r) => r.productId), ...lines.map((l) => l.productId)],
    [returnable, lines],
  );
  const { balanceOf: stockBalanceOf, ready: stockReady } =
    useStockBalances(warehouseVendorId, warehouseId, stockProductIds);

  useEffect(() => {
    const headers = authHeaders();
    Promise.all([
      fetch(`${VENDORS_API}/Select`, { headers }).then((r) => (r.ok ? r.json() : [])),
      fetch('/api/representatives/Select', { headers }).then((r) => (r.ok ? r.json() : [])),
      fetch('/api/warehouses/Select', { headers }).then((r) => (r.ok ? r.json() : [])),
    ]).then(([v, r, w]) => {
      setVendors(unwrapLookupItems(v));
      setRepresentatives(unwrapLookupItems(r));
      setWarehouses(unwrapLookupItems(w));
    }).catch(() => {});
  }, []);

  const labelOf = useCallback((list, id) => {
    const x = list.find((o) => o.id === id);
    if (!x) return '';
    return x.name ? `${x.textId ? `${x.textId} - ` : ''}${x.name}` : (x.textId || '');
  }, []);

  // Satıcı seçilince kalan iade edilebilir satırları çek + eklenen kalemleri sıfırla.
  useEffect(() => {
    setLines([]);
    setReturnable([]);
    if (!vendorId) return;
    let cancelled = false;
    setLoadingReturnable(true);
    fetch(`${VENDORS_API}/${vendorId}/purchase-documents?returnableOnly=false`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then((body) => {
        if (cancelled) return;
        const data = unwrap(body);
        setReturnable(Array.isArray(data?.items) ? data.items : []);
      })
      .catch(() => { if (!cancelled) setReturnable([]); })
      .finally(() => { if (!cancelled) setLoadingReturnable(false); });
    return () => { cancelled = true; };
  }, [vendorId]);

  const addedKeys = useMemo(
    () => new Set(lines.map((l) => `${l.purchaseInvoiceId}|${l.productId}`)),
    [lines],
  );

  const addLine = useCallback((row) => {
    const key = `${row.purchaseInvoiceId}|${row.productId}`;
    if (addedKeys.has(key)) return;
    setLines((prev) => [...prev, {
      key: newKey(),                       // = ReturnDetail.Id (foto bununla iliştirilir)
      purchaseInvoiceId: row.purchaseInvoiceId,
      purchaseInvoiceNo: row.purchaseInvoiceNo,
      purchaseInvoiceDate: row.purchaseInvoiceDate,
      productId: row.productId,
      productLabel: [row.productCode, row.productName].filter(Boolean).join(' - '),
      unit: row.unit,
      unitPrice: row.unitPrice,
      remaining: Number(row.remainingQuantity) || 0,
      quantity: String(Number(row.remainingQuantity) || 0),
      damageReason: '',
      photos: [],
    }]);
  }, [addedKeys]);

  const updateLine = (key, patch) => setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  const removeLine = (key) => setLines((prev) => prev.filter((l) => l.key !== key));

  const onAddPhotos = useCallback(async (key, fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    try {
      const added = [];
      for (const f of files) {
        // eslint-disable-next-line no-await-in-loop
        const dataUri = await fileToCompressedDataUri(f);
        added.push({ key: newKey(), dataUri, sizeBytes: approxBytes(dataUri), id: null });
      }
      setLines((prev) => prev.map((l) => (l.key === key ? { ...l, photos: [...l.photos, ...added] } : l)));
    } catch (e) {
      showToast('error', e.message || 'Fotoğraf eklenemedi');
    }
  }, [showToast]);

  const removePhoto = (lineKey, photoKey) => setLines((prev) => prev.map((l) => (
    l.key === lineKey ? { ...l, photos: l.photos.filter((p) => p.key !== photoKey) } : l
  )));

  const totals = useMemo(() => ({
    lines: lines.length,
    quantity: lines.reduce((n, l) => n + (Number(l.quantity) || 0), 0),
    amount: lines.reduce((n, l) => n + (Number(l.quantity) || 0) * (Number(l.unitPrice) || 0), 0),
  }), [lines]);

  const validate = () => {
    if (!vendorId) return 'Satıcı cari seçiniz';
    if (!representativeId) return 'Plasiyer seçiniz';
    if (!warehouseId) return 'Araç deposu seçiniz';
    if (lines.length === 0) return 'En az bir iade kalemi ekleyin';
    for (const l of lines) {
      const q = Number(l.quantity);
      if (!Number.isFinite(q) || q <= 0) return `Geçerli bir miktar giriniz: ${l.productLabel}`;
      if (q > l.remaining + 0.0001) return `${l.productLabel}: en fazla ${fmtQty(l.remaining)} iade edilebilir`;
      if (isScrap && !l.damageReason) return `${l.productLabel}: bozuk iadede hasar nedeni zorunlu`;
      if (isScrap && l.photos.length === 0) return `${l.productLabel}: bozuk iadede en az 1 fotoğraf zorunlu`;
    }
    return null;
  };

  const uploadPhoto = async (returnDetailId, photo) => {
    const res = await fetch(`${API_BASE}/detail-photo`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({
        returnDetailId, dataUri: photo.dataUri, contentType: 'image/jpeg', sizeBytes: photo.sizeBytes,
      }),
    });
    const body = await res.json().catch(() => null);
    if (!res.ok) throw new Error(body?.message || body?.Message || 'Fotoğraf yüklenemedi');
    return unwrap(body)?.id;
  };

  const save = async (statusValue) => {
    const err = validate();
    if (err) { showToast('error', err); return; }
    setSaving(true);
    try {
      // 1) Bozuk iade fotoğrafları — kalem Id'sine önce yüklenir (henüz yüklenmemiş olanlar).
      const workingLines = lines.map((l) => ({ ...l, photos: l.photos.map((p) => ({ ...p })) }));
      if (isScrap) {
        for (const l of workingLines) {
          for (const p of l.photos) {
            if (p.id) continue;
            // eslint-disable-next-line no-await-in-loop
            p.id = await uploadPhoto(l.key, p);
          }
        }
        // Yüklenen id'leri ekranda da sakla (kayıt tekrar denenirse yeniden yüklenmesin).
        setLines(workingLines);
      }

      // 2) Belge kaydı.
      const payload = {
        return: {
          id: null,
          customerId: null,
          representativeId,
          vendorId,
          warehouseId,
          documentDate: toDateTimeOffset(docDate),
          returnType: RETURN_TYPE_VENDOR,
          returnSubType: subType,
          notes: notes || null,
          status: statusValue,
        },
        details: workingLines.map((l) => ({
          id: l.key,
          productId: l.productId,
          quantity: Number(l.quantity),
          unit: l.unit || null,
          unitPrice: Number(l.unitPrice) || 0,
          damageReason: isScrap ? l.damageReason : null,
          photoIds: isScrap ? l.photos.map((p) => p.id).filter(Boolean) : [],
          allocations: [{ purchaseInvoiceId: l.purchaseInvoiceId, quantity: Number(l.quantity) }],
        })),
      };

      const res = await fetch(`${API_BASE}/SaveWithDetails`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(payload),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.message || body?.Message || 'Belge kaydedilemedi');

      const data = unwrap(body);
      showToast('success', statusValue === RETURN_STATUS.GONDERILDI
        ? 'Belge kesildi — stok ve satıcı carisi işlendi.'
        : 'Taslak kaydedildi.');
      const newId = data?.id;
      setTimeout(() => navigate(newId ? `/vendor-returns/${newId}` : '/vendor-returns'), 700);
    } catch (e) {
      showToast('error', e.message || 'İşlem sırasında hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={s.container}>
      <div style={{ marginBottom: 16, display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <button type="button" style={{ ...s.btnSecondary, marginTop: 2 }} onClick={() => navigate('/vendor-returns')}>
          ← Listeye Dön
        </button>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: 'var(--text-primary)' }}>Yeni Satıcıya İade</h2>
          <p style={{ margin: '4px 0 0', color: 'var(--text-secondary)', fontSize: 14 }}>
            Kalemler satıcının <b>kalan iade edilebilir</b> alış faturası satırlarından seçilir — iade yalnız alış
            faturasına dayandırılabilir (GİB zorunluluğu).
          </p>
        </div>
      </div>

      {/* ── Başlık ── */}
      <div style={card}>
        <h3 style={sectionTitle}>Belge Bilgileri</h3>
        <div style={s.formRow}>
          <div style={s.formGroup}>
            <label style={s.label}>Satıcı Cari *</label>
            <RelationPicker
              config={RELATION_CONFIGS.vendor}
              value={vendorId}
              selectedLabel={vendorId ? labelOf(vendors, vendorId) : ''}
              onChange={(val) => setVendorId(val || '')}
              placeholder="Satıcı seçiniz"
            />
          </div>
          <div style={s.formGroup}>
            <label style={s.label}>Plasiyer *</label>
            <RelationPicker
              config={RELATION_CONFIGS.representative}
              value={representativeId}
              selectedLabel={representativeId ? labelOf(representatives, representativeId) : ''}
              onChange={(val) => setRepresentativeId(val || '')}
              placeholder="Plasiyer seçiniz"
            />
          </div>
          <div style={s.formGroup}>
            <label style={s.label}>Araç Deposu *</label>
            <RelationPicker
              config={RELATION_CONFIGS.warehouse}
              value={warehouseId}
              selectedLabel={warehouseId ? labelOf(warehouses, warehouseId) : ''}
              onChange={(val) => setWarehouseId(val || '')}
              placeholder="Depo seçiniz"
            />
          </div>
        </div>
        <div style={s.formRow}>
          <div style={s.formGroup}>
            <label style={s.label}>Alt Tip *</label>
            <select style={s.select} value={subType} onChange={(e) => setSubType(e.target.value)}>
              {SUB_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <span style={{ ...muted, fontSize: 12, marginTop: 4 }}>{SUB_TYPE[subType]?.hint}</span>
          </div>
          <div style={s.formGroup}>
            <label style={s.label}>Tarih *</label>
            <input style={s.input} type="date" value={docDate} onChange={(e) => setDocDate(e.target.value)} />
          </div>
          <div style={{ ...s.formGroup, flex: 2 }}>
            <label style={s.label}>Not</label>
            <input style={s.input} value={notes} maxLength={400} onChange={(e) => setNotes(e.target.value)} placeholder="Opsiyonel" />
          </div>
        </div>
      </div>

      {/* ── İade edilebilir alış faturası satırları (seçim) ── */}
      <div style={card}>
        <h3 style={sectionTitle}>İade Edilebilir Alış Faturası Satırları</h3>
        {!vendorId ? (
          <div style={{ ...muted, fontSize: 13, padding: '8px 2px' }}>Önce bir satıcı cari seçiniz.</div>
        ) : loadingReturnable ? (
          <div style={{ ...muted, fontSize: 13, padding: '8px 2px' }}>Yükleniyor…</div>
        ) : returnable.length === 0 ? (
          <div style={{ ...muted, fontSize: 13, padding: '8px 2px' }}>
            Bu satıcı için (son 90 gün) iade edilebilir alış faturası satırı bulunamadı.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
                  <th style={th}>Ürün</th>
                  <th style={th}>Fatura No</th>
                  <th style={th}>Fatura Tarihi</th>
                  <th style={{ ...th, ...num }}>Kalan</th>
                  {/* #748 Stok, miktar kolonundan HEMEN SONRA kendi kolonunda (rozet kaldırıldı). */}
                  <th style={{ ...th, ...num }} title="Belgede seçili depodaki kullanılabilir stok">Stok</th>
                  <th style={{ ...th, ...num }}>Birim Fiyat</th>
                  <th style={th}></th>
                </tr>
              </thead>
              <tbody>
                {returnable.map((row) => {
                  const key = `${row.purchaseInvoiceId}|${row.productId}`;
                  const added = addedKeys.has(key);
                  return (
                    <tr key={key} style={{ opacity: row.selectable ? 1 : 0.55 }}>
                      <td style={td}>{[row.productCode, row.productName].filter(Boolean).join(' - ')}</td>
                      <td style={{ ...td, ...mono }}>{row.purchaseInvoiceNo || '—'}</td>
                      <td style={td}>{fmtDate(row.purchaseInvoiceDate)}</td>
                      <td style={{ ...td, ...num, fontWeight: 800 }}>{fmtQty(row.remainingQuantity)} {row.unit || ''}</td>
                      {/* Talep 748: eklemeden ÖNCE seçili depodaki stok görünür. */}
                      <td style={{ ...td, ...num }}><StockCell productId={row.productId} balanceOf={stockBalanceOf} ready={stockReady} /></td>
                      <td style={{ ...td, ...num }}>{fmtMoney(row.unitPrice)}</td>
                      <td style={{ ...td, textAlign: 'center' }}>
                        {row.selectable ? (
                          <button
                            type="button"
                            style={{ ...s.btnSecondary, padding: '4px 12px', opacity: added ? 0.5 : 1 }}
                            disabled={added}
                            onClick={() => addLine(row)}
                          >
                            {added ? 'Eklendi' : 'Ekle'}
                          </button>
                        ) : (
                          <span style={{ ...danger, fontSize: 12 }} title={row.notSelectableReason}>
                            {row.notSelectableReason}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Seçilen iade kalemleri ── */}
      <div style={card}>
        <h3 style={sectionTitle}>İade Kalemleri</h3>
        {lines.length === 0 ? (
          <div style={{ ...muted, fontSize: 13, padding: '8px 2px' }}>
            Henüz kalem eklenmedi. Yukarıdaki listeden “Ekle” ile seçim yapın.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
                  <th style={th}>Ürün</th>
                  <th style={th}>Fatura No</th>
                  <th style={{ ...th, ...num }}>Kalan</th>
                  <th style={{ ...th, ...num }}>İade Miktarı *</th>
                  {/* #748 Stok, miktar kolonundan HEMEN SONRA. */}
                  <th style={{ ...th, ...num }} title="Belgede seçili depodaki kullanılabilir stok">Stok</th>
                  <th style={{ ...th, ...num }}>Birim Fiyat</th>
                  <th style={{ ...th, ...num }}>Tutar</th>
                  <th style={th}></th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l) => {
                  const q = Number(l.quantity) || 0;
                  const over = q > l.remaining + 0.0001;
                  return [
                    <tr key={l.key}>
                      <td style={{ ...td, fontWeight: 600 }}>{l.productLabel}</td>
                      <td style={{ ...td, ...mono }}>{l.purchaseInvoiceNo || '—'}</td>
                      <td style={{ ...td, ...num }}>{fmtQty(l.remaining)} {l.unit || ''}</td>
                      <td style={{ ...td, ...num }}>
                        <input
                          style={{ ...s.input, width: 110, textAlign: 'right', ...(over ? { borderColor: 'var(--danger, #e2445c)' } : null) }}
                          type="number" step="any" min="0" max={l.remaining}
                          value={l.quantity}
                          onChange={(e) => updateLine(l.key, { quantity: e.target.value })}
                        />
                      </td>
                      {/* Talep 748: eklenmiş kalemde de depo stoğu görünür — kendi kolonunda. */}
                      <td style={{ ...td, ...num }}><StockCell productId={l.productId} balanceOf={stockBalanceOf} ready={stockReady} /></td>
                      <td style={{ ...td, ...num }}>{fmtMoney(l.unitPrice)}</td>
                      <td style={{ ...td, ...num }}>{fmtMoney(q * (Number(l.unitPrice) || 0))}</td>
                      <td style={{ ...td, textAlign: 'center' }}>
                        <button
                          type="button" title="Kaldır"
                          style={{ background: '#fee2e2', color: '#b91c1c', border: 'none', borderRadius: 6, padding: '4px 10px', fontWeight: 700, cursor: 'pointer' }}
                          onClick={() => removeLine(l.key)}
                        >×</button>
                      </td>
                    </tr>,
                    // SCRAP: hasar nedeni + foto alt satırı.
                    isScrap ? (
                      <tr key={`${l.key}-scrap`}>
                        <td style={{ ...td, background: 'var(--bg-subtle, rgba(15,23,42,0.02))' }} colSpan={8}>
                          <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                              <label style={{ ...s.label, marginBottom: 0 }}>Hasar Nedeni *</label>
                              <select
                                style={{ ...s.select, minWidth: 200 }}
                                value={l.damageReason}
                                onChange={(e) => updateLine(l.key, { damageReason: e.target.value })}
                              >
                                <option value="">— Seçiniz —</option>
                                {DAMAGE_REASON_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                              </select>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                              <label style={{ ...s.label, marginBottom: 0 }}>
                                Fotoğraf * <span style={muted}>({l.photos.length})</span>
                              </label>
                              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                                {l.photos.map((p) => (
                                  <span key={p.key} style={{ position: 'relative', display: 'inline-block' }}>
                                    <img src={p.dataUri} alt="Kanıt" style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--border-color)' }} />
                                    <button
                                      type="button" title="Sil"
                                      onClick={() => removePhoto(l.key, p.key)}
                                      style={{ position: 'absolute', top: -6, right: -6, width: 20, height: 20, borderRadius: 999, border: 'none', background: 'var(--danger)', color: '#fff', fontWeight: 700, cursor: 'pointer', lineHeight: 1 }}
                                    >×</button>
                                  </span>
                                ))}
                                <label style={{ ...s.btnSecondary, cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}>
                                  + Fotoğraf
                                  <input
                                    type="file" accept="image/*" multiple style={{ display: 'none' }}
                                    onChange={(e) => { onAddPhotos(l.key, e.target.files); e.target.value = ''; }}
                                  />
                                </label>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    ) : null,
                  ];
                })}
              </tbody>
            </table>
            <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end', gap: 24, fontSize: 13, ...muted }}>
              <span>Kalem: <strong style={{ color: 'var(--text-primary)' }}>{totals.lines}</strong></span>
              <span>Toplam Miktar: <strong style={{ color: 'var(--text-primary)' }}>{fmtQty(totals.quantity)}</strong></span>
              <span>Tutar: <strong style={{ color: 'var(--text-primary)' }}>{fmtMoney(totals.amount)}</strong> <span style={{ fontSize: 11 }}>(KDV hariç, tahmini)</span></span>
            </div>
          </div>
        )}
      </div>

      {canAdd && (
        <div style={{ display: 'flex', gap: 10 }}>
          <button style={{ ...s.btnSecondary, opacity: saving ? 0.6 : 1 }} onClick={() => save(RETURN_STATUS.TASLAK)} disabled={saving}>
            {saving ? 'Kaydediliyor…' : 'Taslak Kaydet'}
          </button>
          <button style={{ ...s.btnPrimary, opacity: saving ? 0.6 : 1 }} onClick={() => save(RETURN_STATUS.GONDERILDI)} disabled={saving}>
            {saving ? 'İşleniyor…' : 'Kes ve Gönder'}
          </button>
        </div>
      )}

      <Toast toast={toast} />
    </div>
  );
}

export default VendorReturnCreatePage;
